const AUTH_MESSAGE_TYPE = 'NUJIANG_AUTH_CONTEXT';
const AUTH_CONTEXT_EVENT = 'nujiang-auth-context';
const STORAGE_KEY = 'nujiang-auth-context';
const TOKEN_QUERY_KEYS = ['token', 'access_token', 'bladeAuth', 'Blade-Auth'];
const BIG_SCREEN_TOKEN_QUERY_KEYS = ['bigScreenToken', 'screenToken'];
const TRUSTED_PARENT_ORIGINS = ['https://www.lssnkj.com'];
const TRUSTED_IFRAME_ORIGINS = ['https://www.lssnkj.com', 'https://dp.lssnkj.com'];
const STATIC_IFRAME_ORIGIN = 'https://www.lssnkj.com';
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '::1'];
const LOCAL_STATIC_IFRAME_PORT = '26001';
const STATIC_IFRAME_FILES = [
  'map.html',
  'screen-video.html',
  'showcase-drone-live.html',
  'showcase-map.html',
  'showcase-monitor.html',
  'showcase-monitor-single.html',
  'ys-video.html',
];
const AUTH_POST_LIMIT = 20;
const AUTH_POST_INTERVAL = 300;

let initialized = false;

const isBrowser = () => typeof window !== 'undefined';

const isLocalOrigin = origin => {
  try {
    const url = new URL(origin);
    return LOCAL_HOSTS.includes(url.hostname);
  } catch (error) {
    return false;
  }
};

const trustedParentOrigin = origin => TRUSTED_PARENT_ORIGINS.includes(origin) || isLocalOrigin(origin);

const iframeFileName = pathname => {
  const parts = (pathname || '').split('/');
  return parts[parts.length - 1] || '';
};

const isStaticIframePage = pathname => STATIC_IFRAME_FILES.includes(iframeFileName(pathname));

const normalizeStaticIframePath = pathname => `/iframe/${iframeFileName(pathname)}`;

const localStaticIframeUrl = target => new URL(
  `${iframeFileName(target.pathname)}${target.search}${target.hash}`,
  `http://127.0.0.1:${LOCAL_STATIC_IFRAME_PORT}/`
).toString();

const isLocalStaticIframeUrl = target => (
  LOCAL_HOSTS.includes(target.hostname) &&
  target.port === LOCAL_STATIC_IFRAME_PORT &&
  isStaticIframePage(target.pathname)
);

export const normalizeIframeUrl = url => {
  if (!url || !isBrowser()) return url;
  try {
    const target = new URL(url, window.location.origin);
    if (!isStaticIframePage(target.pathname)) return url;

    if (isLocalOrigin(window.location.origin)) {
      if (isLocalStaticIframeUrl(target)) return url;
      if (isLocalOrigin(target.origin) || TRUSTED_IFRAME_ORIGINS.includes(target.origin)) {
        return localStaticIframeUrl(target);
      }
    }

    if (isLocalStaticIframeUrl(target) || TRUSTED_IFRAME_ORIGINS.includes(target.origin)) {
      return new URL(
        `${normalizeStaticIframePath(target.pathname)}${target.search}${target.hash}`,
        STATIC_IFRAME_ORIGIN
      ).toString();
    }

    return url;
  } catch (error) {
    return url;
  }
};

const trustedIframeUrl = url => {
  try {
    const nextUrl = normalizeIframeUrl(url);
    const target = new URL(nextUrl, window.location.origin);
    if (isLocalOrigin(target.origin)) return isLocalOrigin(window.location.origin);
    return TRUSTED_IFRAME_ORIGINS.includes(target.origin) && target.pathname.startsWith('/iframe/');
  } catch (error) {
    return false;
  }
};

const readStorageContext = storage => {
  try {
    return JSON.parse(storage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
};

const readContext = () => {
  if (!isBrowser()) return {};
  return Object.assign(
    {},
    readStorageContext(window.localStorage),
    readStorageContext(window.sessionStorage)
  );
};

const writeContext = context => {
  if (!isBrowser()) return {};
  const next = Object.assign({}, readContext(), context);
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // Web storage can be unavailable in private or restricted browser contexts.
  }
  window.dispatchEvent(new CustomEvent(AUTH_CONTEXT_EVENT, { detail: next }));
  return next;
};

export const getQueryTenantId = () => {
  if (!isBrowser()) return '';
  const query = new URLSearchParams(window.location.search);
  return (query.get('tenantId') || query.get('tenant_id') || '').trim();
};

const getQueryToken = () => {
  if (!isBrowser() || !isLocalOrigin(window.location.origin)) return '';
  const query = new URLSearchParams(window.location.search);
  const key = TOKEN_QUERY_KEYS.find(name => query.get(name));
  return key ? (query.get(key) || '').trim() : '';
};

const getQueryBigScreenToken = () => {
  if (!isBrowser()) return '';
  const query = new URLSearchParams(window.location.search);
  const key = BIG_SCREEN_TOKEN_QUERY_KEYS.find(name => query.get(name));
  return key ? (query.get(key) || '').trim() : '';
};

const cleanAuthQuery = () => {
  if (!isBrowser() || !window.history || !isLocalOrigin(window.location.origin)) return;
  const url = new URL(window.location.href);
  let changed = false;
  TOKEN_QUERY_KEYS.forEach(key => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }
};

export const getAuthContext = () => {
  const stored = readContext();
  const tenantId = stored.tenantId || getQueryTenantId();
  return {
    tenantId: tenantId || '',
    token: stored.token || '',
    bigScreenToken: stored.bigScreenToken || getQueryBigScreenToken() || '',
  };
};

export const normalizeToken = token => {
  const value = (token || '').trim();
  if (!value) return '';
  if (/^(bearer|basic|crypto)\s+/i.test(value)) return value;
  return `bearer ${value}`;
};

export const getAuthHeaders = () => {
  const context = getAuthContext();
  const headers = {
    'Blade-Requested-With': 'BladeHttpRequest',
  };
  if (context.tenantId) {
    headers['Tenant-Id'] = context.tenantId;
    headers['Blade-TenantId'] = context.tenantId;
  }
  if (context.token) {
    headers['Blade-Auth'] = normalizeToken(context.token);
  }
  if (context.bigScreenToken) {
    headers['Blade-BigScreen-Token'] = context.bigScreenToken;
  }
  return headers;
};

export const targetOrigin = url => {
  try {
    const nextUrl = normalizeIframeUrl(url);
    const target = new URL(nextUrl, window.location.origin);
    return trustedIframeUrl(nextUrl) ? target.origin : '';
  } catch (error) {
    return '';
  }
};

export const appendTenantToUrl = url => {
  const context = getAuthContext();
  const nextUrl = normalizeIframeUrl(url);
  if (!nextUrl || !context.tenantId || !targetOrigin(nextUrl)) return nextUrl;
  try {
    const target = new URL(nextUrl, window.location.origin);
    target.searchParams.set('tenantId', context.tenantId);
    if (context.bigScreenToken) {
      target.searchParams.set('bigScreenToken', context.bigScreenToken);
    }
    return target.toString();
  } catch (error) {
    return url;
  }
};

export const postAuthContextToFrame = iframe => {
  if (!iframe || !iframe.contentWindow) return;
  const origin = targetOrigin(iframe.src);
  if (!origin) return;
  const context = getAuthContext();
  if (!context.tenantId && !context.token && !context.bigScreenToken) return;
  iframe.contentWindow.postMessage(
    Object.assign({ type: AUTH_MESSAGE_TYPE }, context),
    origin
  );
};

export const scheduleAuthContextToFrame = iframe => {
  if (!iframe || !iframe.contentWindow) return;
  let count = 0;
  postAuthContextToFrame(iframe);
  const timer = window.setInterval(() => {
    count += 1;
    postAuthContextToFrame(iframe);
    if (count >= AUTH_POST_LIMIT || !iframe.contentWindow) {
      window.clearInterval(timer);
    }
  }, AUTH_POST_INTERVAL);
};

const handleAuthMessage = event => {
  if (!trustedParentOrigin(event.origin)) return;
  const data = event.data || {};
  if (data.type !== AUTH_MESSAGE_TYPE) return;
  writeContext({
    tenantId: data.tenantId || '',
    token: data.token || '',
    bigScreenToken: data.bigScreenToken || '',
  });
};

export const initAuthBridge = () => {
  if (!isBrowser() || initialized) return;
  initialized = true;
  const tenantId = getQueryTenantId();
  const token = getQueryToken();
  const bigScreenToken = getQueryBigScreenToken();
  const queryContext = {};
  if (tenantId) queryContext.tenantId = tenantId;
  if (token) queryContext.token = token;
  if (bigScreenToken) queryContext.bigScreenToken = bigScreenToken;
  if (Object.keys(queryContext).length) {
    writeContext(queryContext);
    cleanAuthQuery();
  }
  window.addEventListener('message', handleAuthMessage, false);
};

export { AUTH_CONTEXT_EVENT };
